/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
-----------------------------------------------------------------------------*/

<template>
  <div class="row">
    <div class="col-sm-6 col-sm-offset-3">
      <h1 id="signUpH1">
        Sign Up
      </h1>
      <p class="help-block">
        If you already have an account, sign in with the link in the top right.
      </p>
      <form
        v-if="!invite"
        aria-labelledby="signUpH1"
        @submit.prevent="checkInvite"
      >
        <div class="form-group">
          <label
            for="signUpInviteCode"
            class="control-label"
          >
            Invite Code
          </label>
          <input
            id="signUpInviteCode"
            v-model.trim="formValues.inviteCode"
            type="text"
            class="form-control"
            placeholder="invite code"
            name="code"
            required
            @change="updateLoginLink"
          >
        </div>
        <div class="help-block">
          <p
            v-if="errors.invalidInvite"
            class="text-danger"
            role="alert"
          >
            {{ inviteError }}
          </p>
          <p>
            If you have an Invite Code from your instructor, enter it here.<br>
            If you have a link, copy and paste it into your browser.
          </p>
        </div>
        <loading-button :loading="checkingInvite">
          Continue
        </loading-button>
        <hr>
        <p>
          Don't have an invite code? Check your class instructions for information.
          You must first receive a link or code from your instructor or plan administrator to continue.
        </p>
        <p>
          Need Help or want to request information for your organization? <a
            href="http://www.myvoicevibes.com/help.html"
            target="_blank"
          >
            Contact VoiceVibes
          </a>
        </p>
      </form>
      <form
        v-if="invite"
        aria-labelledby="signUpH1"
        @submit.prevent="signUp"
      >
        <h2>Login Information</h2>
        <div class="form-group">
          <label
            for="signUpEmail"
            class="control-label"
          >
            Email address
          </label>
          <input
            id="signUpEmail"
            v-model.trim="formValues.email"
            type="email"
            class="form-control"
            placeholder="email"
            name="email"
            required
            @keypress="errors.alreadyExists=false"
          >
        </div>
        <div class="help-block">
          <p
            v-if="errors.alreadyExists"
            class="text-danger"
            role="alert"
          >
            Email already registered. Please
            <router-link :to="signInLink">
              sign into your account here
            </router-link>
            to join this new group.
          </p>
        </div>
        <div class="form-group">
          <label
            for="signUpPassword"
            class="control-label"
          >
            Password
          </label>
          <input
            id="signUpPassword"
            v-model.trim="formValues.password"
            type="password"
            class="form-control"
            placeholder="password"
            required
          >
        </div>
        <h2>A little about you</h2>
        <div
          class="form-group"
        >
          <label
            for="signUpFirstName"
            class="control-label"
          >
            First name
          </label>
          <input
            id="signUpFirstName"
            v-model.trim="formValues.firstName"
            type="text"
            class="form-control"
            placeholder=""
            name="first"
            required
            maxlength="50"
          >
        </div>
        <div
          class="form-group"
        >
          <label
            for="signUpLastName"
            class="control-label"
          >
            Last name
          </label>
          <input
            id="signUpLastName"
            v-model.trim="formValues.lastName"
            type="text"
            class="form-control"
            placeholder=""
            name="last"
            required
            maxlength="50"
          >
        </div>
        <div
          class="form-group"
        >
          <label
            for="signUpName"
            class="control-label"
          >
            Full name
          </label>
          <input
            id="signUpName"
            v-model.trim="formValues.name"
            type="text"
            class="form-control"
            placeholder=""
            required
            maxlength="101"
            @keypress="disableAutoname"
          >
          <p
            v-if="autoName"
            class="help-block"
          >
            This is auto-generated, but you can edit it if you need to
          </p>
        </div>
        <div class="form-group">
          <label
            for="signUpTitle"
            class="control-label"
          >
            Title/Position
          </label>
          <select
            id="signUpTitle"
            v-model="formValues.metadata.title"
            class="form-control"
            name="title"
            required
          >
            <option value="">
              Select...
            </option>
            <option value="college-student">
              College Student
            </option>
            <option value="hs-student">
              High School Student
            </option>
            <option value="professor">
              Professor/Instructor
            </option>
            <option value="k12-teacher">
              K-12 Teacher
            </option>
            <option value="dept-head">
              Department Head
            </option>
            <option value="school-admin">
              School Administrator
            </option>
            <option value="coach">
              Corporate Trainer/Coach
            </option>
            <option value="manager">
              Manager
            </option>
            <option value="director-executive">
              Director/Executive
            </option>
            <option value="professional">
              Professional
            </option>
            <option value="other">
              Other/No Title
            </option>
          </select>
        </div>
        <h2>More about you (optional info to help us improve our algorithms)</h2>
        <div class="form-group">
          <label
            for="signUpBirthyear"
            class="control-label"
          >
            Birth year
          </label>
          <select
            id="signUpBirthyear"
            v-model.number="formValues.metadata.birthyear"
            class="form-control"
            name="birthyear"
          >
            <option :value="undefined">
              Select...
            </option>
            <option
              v-for="n in 99"
              v-if="n >= 13"
              :value="new Date().getFullYear()-n"
            >
              {{ new Date().getFullYear()-n }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label
            for="signUpGender"
            class="control-label"
          >
            Gender
          </label>
          <select
            id="signUpGender"
            v-model="formValues.metadata.gender"
            class="form-control"
            name="gender"
          >
            <option :value="undefined">
              Select...
            </option>
            <option value="f">
              Female
            </option>
            <option value="m">
              Male
            </option>
            <option value="o">
              Other
            </option>
          </select>
        </div>

        <input
          id="isEu1"
          v-model="formValues.isEu"
          type="hidden"
          name="isEu"
          value="0"
        >

        <div
          v-if="formValues.isEu !== '1'"
          class="form-group"
        >
          <div class="checkbox">
            <label
              for="signupTerms"
              class="control-label lower"
            >
              <input
                id="signupTerms"
                v-model="formValues.termsAccepted"
                type="checkbox"
                required
              >
              I have read and agree with the
              <a
                href="http://www.myvoicevibes.com/terms.html"
                target="_blank"
              >Terms of Use</a> and
              <a
                href="https://www.myvoicevibes.com/privacy-policy.html"
                target="_blank"
              >Privacy Policy</a>.
            </label>
          </div>
        </div>

        <div v-if="formValues.isEu === '1'">
          <h2>Consent to Store and Use Your Information</h2>
          As a resident of the European Union, you have certain rights to control how
          your data is stored and used. Please refer to the "ADDITIONAL INFORMATION FOR EU, UK AND SWITZERLAND BASED USERS"
          section in our
          <a
            href="https://www.myvoicevibes.com/privacy-policy.html"
            target="_blank"
          >Privacy Policy</a> for details.
          <div class="form-group">
            <div class="checkbox">
              <label
                for="age"
                class="control-label lower"
              >
                <input
                  id="age"
                  v-model="formValues.ageAttestation"
                  type="checkbox"
                  required
                >
                I am at least 16 years of age. REQUIRED
              </label>
            </div>
            <div class="checkbox">
              <label
                for="dataExport"
                class="control-label lower"
              >
                <input
                  id="dataExport"
                  v-model="formValues.dataExport"
                  type="checkbox"
                  required
                >
                I agree to allow my personal information and any audio recordings I create with VoiceVibes
                to be stored and processed in the United States of America (USA). REQUIRED
              </label>
            </div>
            <div class="checkbox">
              <label
                for="signupTerms"
                class="control-label lower"
              >
                <input
                  id="signupTerms"
                  v-model="formValues.termsAccepted"
                  type="checkbox"
                  required
                >
                I have read and agree with the
                <a
                  href="http://www.myvoicevibes.com/terms.html"
                  target="_blank"
                >Terms of Use</a>.
                REQUIRED
              </label>
            </div>
            <div class="checkbox">
              <label
                for="privacyTerms"
                class="control-label lower"
              >
                <input
                  id="privacyTerms"
                  v-model="formValues.privacyAccepted"
                  type="checkbox"
                  required
                >
                I have read and agree with the
                <a
                  href="https://www.myvoicevibes.com/privacy-policy.html"
                  target="_blank"
                >Privacy Policy</a>.
                REQUIRED
              </label>
            </div>
            <div class="checkbox">
              <label
                for="dataRetention"
                class="control-label lower"
              >
                <input
                  id="dataRetention"
                  v-model="formValues.dataRetention"
                  type="checkbox"
                >
                I agree to allow my personal information and any audio recordings in my VoiceVibes account
                to be stored for a period of up to 5 years after my account has closed.  This allows your
                data to remain with VoiceVibes and enables you to reopen your account after it expires
                and in order to help us to provide you with an improved quality of service. OPTIONAL
              </label>
            </div>
          </div>
        </div>

        <div class="help-block">
          <p
            v-if="errors.invalidEntry"
            class="text-danger"
            role="alert"
          >
            Some of the data you entered is not valid
          </p>
          <p
            v-if="errors.paymentRequired"
            class="text-danger"
            role="alert"
          >
            The organization you are trying to join
            has reached its maximum number of licenses.
            Please contact your manager or VoiceVibes administrator to
            request access.
          </p>
        </div>

        <loading-button :loading="signingUp">
          Sign up
        </loading-button>
      </form>
    </div>
  </div>
</template>

<style lang="less" scoped>
  div.checkbox input {
    margin-top: 1px;
  }
</style>

<script>
export default {
  data() {
    return {
      signingUp: false,
      checkingInvite: false,
      errors: {
        alreadyExists: false,
        invalidEntry: false,
        paymentRequired: false,
        invalidInvite: false,
        inviteError: 'This Invite Code is not valid',
      },
      formValues: {
        email: this.$route.query.email || '',
        password: '',
        termsAccepted: false,
        privacyAccepted: false,
        firstName: '',
        lastName: '',
        isEu: '0',
        name: '',
        metadata: {
          birthyear: undefined,
          gender: undefined,
          title: ''
        },
        inviteCode: this.$route.query.code,
        inviteId: this.$route.query.invite
      },
      autoName: true,
      loading: true
    };
  },
  computed: {
    signInLink() {
      return this.$store.getters.signInLink;
    },
    invite() {
      return this.$store.getters.signUpInvite;
    },
  },
  watch: {
    'formValues.firstName': 'autoGenerateName',
    'formValues.lastName': 'autoGenerateName'
  },
  created() {
    if (this.formValues.inviteCode || this.formValues.inviteId) {
      this.checkInvite();
    }
  },
  methods: {
    updateLoginLink() {
      let signIn = { name: 'sign-in' };
      if (this.formValues.inviteCode) {
        signIn.query = { code: this.formValues.inviteCode };
      }
      this.$store.dispatch('switchSignInLink', signIn);
    },
    signIn() {
      this.$store.dispatch('resetGroupMembersCache');

      const next = {name: 'confirm-code', query: {code: this.$route.query.code}};
      const signInCreds = {
        email: this.formValues.email,
        password: this.formValues.password,
      };

      this.$store.dispatch('signIn', signInCreds)
        .then(() => {
          this.$router.push(next);
        })
        .catch(() => {
          // Don't do anything if they entered the wrong password
        });
    },
    signUp() {
      const next = this.$route.query.next || '/home';
      this.signingUp = true;
      this.$store.dispatch('signUp', this.formValues)
        .then(() => {
          this.$store.dispatch('resetSignInLink');
          this.$router.push(next);
        })
        .catch(errors => {
          if (errors[0].$status === 409) {
            this.errors.alreadyExists = true;
            window.scrollTo(0,0);
            this.signIn();
          }
          if (errors[0].$status === 400) {
            this.errors.invalidEntry = true;
          }
          if (errors[0].$status === 402) {
            this.errors.paymentRequired = true;
          }
        })
        .finally(() => {
          this.signingUp = false;
        });
    },
    autoGenerateName() {
      if (this.autoName) {
        this.formValues.name = this.formValues.firstName + ' ' + this.formValues.lastName;
      }
    },
    disableAutoname(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      this.autoName = false;
    },
    checkInvite() {
      this.checkingInvite = true;
      this.$store.dispatch('getInvite', this.formValues)
        .then((invite) => {
          this.$store.dispatch('setOrg', invite.org);
          this.formValues.isEu = (invite.isEu === true) ? '1' : '0';
          this.$announcer.set('Sign Up Form has loaded');
          this.$nextTick(() => document.getElementById('signUpEmail').focus());
        })
        .catch(errors => {
          if (errors[0].$status === 400) {
            this.errors.invalidInvite = true;
            this.inviteError = 'This Invite Code is not valid';
            this.$announcer.set('This Invite Code is not valid');
          } else if (errors[0].$status === 401 || errors[0].$status === 402) {
            this.errors.invalidInvite = true;
            this.inviteError = errors[0].message;
            this.$announcer.set(errors[0].message);
          }
        })
        .finally(() => {
          this.checkingInvite = false;
        });
    }
  }
};
</script>
